<template>
  <div style="display: flex; align-items: center; margin: 15px 0">
    <select
      id="state"
      v-model="stateSelected"
      class="form-control form-select"
      style="width: 300px"
      :disabled="loadingStates"
      @change="fetchCities"
      required
    >
      <option :value="null" disabled>Selecione o Estado</option>
      <option v-for="state in states" :key="state.id" :value="state.id">
        {{ state.name }}
      </option>
    </select>

    <select
      v-if="stateSelected"
      id="city"
      v-model="citySelected"
      class="form-control form-select"
      style="width: 300px; margin-left: 15px"
      :disabled="loadingCities"
      @change="emitCity"
      required
    >
      <option :value="null" disabled>Selecione uma cidade</option>
      <option v-for="city in cities" :key="city.id" :value="city.id">
        {{ city.name }}
      </option>
    </select>
  </div>
</template>

<script>
import axios from 'axios'
import { useStateCityStore } from '@/Stores/stateCityStore'

export default {
  name: 'StateCitySelector',
  props: {
    states: {
      type: Array,
      required: true
    },
    defaultState: {
      type: [String, Number],
      default: null,
      validator: function(value) {
        // Aceita string, número ou nulo
        return value === null || typeof value === 'string' || typeof value === 'number';
      }
    },
    defaultCity: {
      type: [String, Number],
      default: null,
      validator: function(value) {
        // Aceita string, número ou nulo
        return value === null || typeof value === 'string' || typeof value === 'number';
      }
    }
  },
  data() {
    const store = useStateCityStore()
    return {
      store,
      stateSelected: null,
      citySelected: null,
      cities: [],
      loadingStates: false,
      loadingCities: false
    }
  },
  created() {
    // Inicializa com valores default, se existirem
    if (this.defaultState !== null && this.defaultState !== undefined) {
      this.stateSelected = this.normalizeValue(this.defaultState);
    }
  },
  mounted() {
    if (this.stateSelected) {
      this.fetchCities();
    }
  },
  watch: {
    defaultState: {
      immediate: true,
      handler(newValue) {
        if (newValue !== null && newValue !== undefined) {
          this.stateSelected = this.normalizeValue(newValue);
          if (this.stateSelected) {
            this.fetchCities();
          }
        }
      }
    },
    defaultCity: {
      immediate: true,
      handler(newValue) {
        if (newValue !== null && newValue !== undefined) {
          // Converte para o formato esperado - será usado após carregar as cidades
          this._pendingCityValue = this.normalizeValue(newValue);
          
          // Se já tiver cidades carregadas, tenta setar imediatamente
          if (this.cities.length > 0 && this._pendingCityValue) {
            this.trySetCity(this._pendingCityValue);
          }
        }
      }
    },
    stateSelected(newValue) {
      if (newValue !== null && newValue !== undefined) {
        const normalizedValue = this.normalizeValue(newValue);
        this.store.setState(normalizedValue);
        this.$emit('update:state', normalizedValue);
      }
    },
    citySelected(newValue) {
      if (newValue === null || newValue === undefined) return;
      
      const selectedCity = this.cities.find(c => {
        return this.normalizeValue(c.id) === this.normalizeValue(newValue);
      });
      
      if (selectedCity) {
        this.store.setCity(selectedCity.name);
        this.$emit('update:city', selectedCity.name);
      }
    }
  },
  methods: {
    // Função para normalizar valores para comparação
    normalizeValue(value) {
      if (value === null || value === undefined) return null;
      
      // Se for número ou string numérica, converte para número
      if (typeof value === 'number') return value;
      if (typeof value === 'string' && !isNaN(Number(value))) return Number(value);
      
      // Caso contrário, mantém como está
      return value;
    },
    
    // Tenta definir a cidade com base em id ou nome
    trySetCity(value) {
      if (!value) return false;
      
      // Tenta encontrar pelo ID primeiro
      const cityById = this.cities.find(c => this.normalizeValue(c.id) === this.normalizeValue(value));
      if (cityById) {
        this.citySelected = cityById.id;
        return true;
      }
      
      // Se não encontrou por ID, tenta pelo nome
      const cityByName = this.cities.find(c => c.name === value);
      if (cityByName) {
        this.citySelected = cityByName.id;
        return true;
      }
      
      return false;
    },
    
    async fetchCities() {
      if (!this.stateSelected) return;
      
      try {
        this.loadingCities = true;
        const response = await axios.get(`/api/cities/${this.stateSelected}`);
        this.cities = response.data;
        this.citySelected = null;
        
        // Se tiver um valor pendente de cidade, tenta aplicá-lo
        if (this._pendingCityValue) {
          this.trySetCity(this._pendingCityValue);
          this._pendingCityValue = null; // Limpa após usar
        } 
        // Se tiver defaultCity, tenta usar
        else if (this.defaultCity) {
          this.trySetCity(this.defaultCity);
        }
      } catch (error) {
        console.error('Erro ao buscar cidades:', error);
      } finally {
        this.loadingCities = false;
      }
    },
    
    emitCity() {
      const selectedCity = this.cities.find(c => c.id === this.citySelected);
      if (selectedCity) {
        this.$emit('update:city', selectedCity.name);
      }
    },
  },
}
</script>
